import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Hotel, Plus, Trash2, CreditCard, ExternalLink, Loader2, Download, CalendarClock,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_TRAVELERS = 6;

const STATUS_STYLES = {
  awaiting_payment: 'bg-amber-100 text-amber-800',
  paid: 'bg-blue-100 text-blue-800',
  booked: 'bg-indigo-100 text-indigo-800',
  delivered: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-600',
  completed: 'bg-gray-100 text-gray-600',
  refused: 'bg-red-100 text-red-700',
};

const todayIso = () => new Date().toISOString().slice(0, 10);

const emptyForm = (destination) => ({
  travelers: [{ first_name: '', last_name: '' }],
  destination_country: destination || '',
  city: '',
  check_in: '',
  check_out: '',
  appointment_date: '',
  whatsapp: '',
  notes: '',
  consent: false,
});

const formatDate = (value, language) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-GB');
};

const formatDateTime = (value, language) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(language === 'fr' ? 'fr-FR' : 'en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

/**
 * Bestellbereich "Hotel-Reservierung für das Visa-Dossier" im Kunden-Dashboard:
 * Formular, Bezahlung (Stripe 15 € / DHMAD 20 TND) und Liste der eigenen
 * Bestellungen mit PDF-Download.
 */
const HotelBookingSection = ({ defaultDestination }) => {
  const { t, language } = useLanguage();
  const [form, setForm] = useState(() => emptyForm(defaultDestination));
  const [bookings, setBookings] = useState([]);
  const [dhmadUrl, setDhmadUrl] = useState('https://dhmad.tn/');
  const [submitting, setSubmitting] = useState(null); // 'stripe' | 'dhmad' | null
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/hotel-bookings`);
      setBookings(res.data.bookings || []);
    } catch {
      // Liste bleibt leer
    }
  }, []);

  // Bestellungen + DHMAD-Link laden
  useEffect(() => {
    fetchBookings();
    axios.get(`${API}/payment/dhmad-hotel-link`)
      .then((res) => res.data?.url && setDhmadUrl(res.data.url))
      .catch(() => {});
  }, [fetchBookings]);

  // Rückkehr von Stripe: Zahlung prüfen und Parameter aus der URL entfernen
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('hotel_session_id');
    if (!sessionId) return;

    params.delete('hotel_session_id');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}#hotel-booking`);

    axios.get(`${API}/hotel-bookings/checkout-status/${sessionId}`)
      .then((res) => {
        if (res.data.payment_status === 'paid') toast.success(t('hotel_paid_success'));
        else toast.info(t('hotel_payment_pending'));
        fetchBookings();
      })
      .catch(() => toast.error(t('hotel_payment_pending')));
  }, [fetchBookings, t]);

  // Bei Aufruf über #hotel-booking zum Bereich scrollen
  useEffect(() => {
    if (window.location.hash === '#hotel-booking') {
      setTimeout(() => document.getElementById('hotel-booking')?.scrollIntoView({ behavior: 'smooth' }), 300);
    }
  }, []);

  useEffect(() => {
    if (defaultDestination) {
      setForm((prev) => (prev.destination_country ? prev : { ...prev, destination_country: defaultDestination }));
    }
  }, [defaultDestination]);

  const setField = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const setTraveler = (index, field, value) => {
    setForm((prev) => ({
      ...prev,
      travelers: prev.travelers.map((tr, i) => (i === index ? { ...tr, [field]: value } : tr)),
    }));
  };

  const addTraveler = () => {
    setForm((prev) => (prev.travelers.length >= MAX_TRAVELERS
      ? prev
      : { ...prev, travelers: [...prev.travelers, { first_name: '', last_name: '' }] }));
  };

  const removeTraveler = (index) => {
    setForm((prev) => ({ ...prev, travelers: prev.travelers.filter((_, i) => i !== index) }));
  };

  const validate = () => {
    const travelersOk = form.travelers.every((tr) => tr.first_name.trim() && tr.last_name.trim());
    if (!travelersOk || !form.destination_country.trim() || !form.city.trim()
      || !form.check_in || !form.check_out || !form.appointment_date) {
      toast.error(t('hotel_error_fill'));
      return false;
    }
    if (form.check_out <= form.check_in) {
      toast.error(t('hotel_error_dates'));
      return false;
    }
    if (!form.consent) {
      toast.error(t('hotel_error_consent'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (paymentMethod) => {
    if (!validate()) return;
    setSubmitting(paymentMethod);
    try {
      const res = await axios.post(`${API}/hotel-bookings`, {
        ...form,
        travelers: form.travelers.map((tr) => ({ first_name: tr.first_name.trim(), last_name: tr.last_name.trim() })),
        whatsapp: form.whatsapp.trim() || null,
        notes: form.notes.trim() || null,
        payment_method: paymentMethod,
        origin_url: window.location.origin,
      });

      if (paymentMethod === 'stripe' && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
        return;
      }

      // DHMAD: Bestellung ist gespeichert -> direkt zum Zahlungslink (wie bei Stripe)
      toast.success(t('hotel_dhmad_created'));
      window.location.href = res.data.dhmad_url || dhmadUrl;
    } catch (error) {
      toast.error(error.response?.data?.detail && typeof error.response.data.detail === 'string'
        ? error.response.data.detail
        : t('hotel_error_create'));
      setSubmitting(null);
    }
  };

  const handleDownload = async (booking) => {
    setDownloadingId(booking.id);
    try {
      const res = await axios.get(`${API}/hotel-bookings/${booking.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `hotel-reservation-${booking.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('PDF');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card id="hotel-booking" className="rounded-sm border-0 shadow-sm scroll-mt-24" data-testid="hotel-booking-card">
      <CardHeader className="pb-4">
        <CardTitle className="font-heading text-lg flex items-center gap-2">
          <Hotel className="w-5 h-5" />
          {t('hotel_section_title')}
        </CardTitle>
        <p className="text-sm text-gray-600">{t('hotel_section_desc')}</p>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* Bestellformular */}
        <div className="space-y-5">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h3 className="font-semibold text-[#0A0A0A]">{t('hotel_new_order')}</h3>
            <span className="text-sm font-bold text-[#0A0A0A]">
              €{t('hotel_price')} <span className="text-gray-400 font-normal">·</span> 🇹🇳 {t('hotel_price_tnd')} TND
            </span>
          </div>

          <div className="space-y-3">
            <Label>{t('hotel_travelers')} *</Label>
            {form.travelers.map((tr, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={index} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2 items-center">
                <Input
                  placeholder={t('hotel_first_name')}
                  value={tr.first_name}
                  maxLength={80}
                  onChange={(e) => setTraveler(index, 'first_name', e.target.value)}
                />
                <Input
                  placeholder={t('hotel_last_name')}
                  value={tr.last_name}
                  maxLength={80}
                  onChange={(e) => setTraveler(index, 'last_name', e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="text-gray-400 hover:text-red-600 justify-self-end"
                  onClick={() => removeTraveler(index)}
                  disabled={form.travelers.length === 1}
                  aria-label={t('hotel_remove')}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            {form.travelers.length < MAX_TRAVELERS && (
              <Button type="button" variant="outline" size="sm" className="rounded-sm" onClick={addTraveler}>
                <Plus className="w-4 h-4 mr-1" />
                {t('hotel_add_traveler')}
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="hotel-country">{t('hotel_country')} *</Label>
              <Input id="hotel-country" value={form.destination_country} maxLength={80}
                onChange={(e) => setField('destination_country', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hotel-city">{t('hotel_city')} *</Label>
              <Input id="hotel-city" value={form.city} maxLength={80}
                onChange={(e) => setField('city', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hotel-check-in">{t('hotel_check_in')} *</Label>
              <Input id="hotel-check-in" type="date" min={todayIso()} value={form.check_in}
                onChange={(e) => setField('check_in', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hotel-check-out">{t('hotel_check_out')} *</Label>
              <Input id="hotel-check-out" type="date" min={form.check_in || todayIso()} value={form.check_out}
                onChange={(e) => setField('check_out', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hotel-appointment">{t('hotel_appointment')} *</Label>
              <Input id="hotel-appointment" type="date" min={todayIso()} value={form.appointment_date}
                onChange={(e) => setField('appointment_date', e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="hotel-whatsapp">{t('hotel_whatsapp')}</Label>
              <Input id="hotel-whatsapp" type="tel" placeholder="+216 …" value={form.whatsapp} maxLength={30}
                onChange={(e) => setField('whatsapp', e.target.value)} />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="hotel-notes">{t('hotel_notes')}</Label>
            <Textarea id="hotel-notes" rows={2} maxLength={500} placeholder={t('hotel_notes_placeholder')}
              value={form.notes} onChange={(e) => setField('notes', e.target.value)} />
          </div>

          <label className="flex items-start gap-3 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-[#FF3B30]"
              checked={form.consent}
              onChange={(e) => setField('consent', e.target.checked)}
            />
            <span>{t('hotel_consent')}</span>
          </label>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Button
              className="h-12 rounded-sm bg-[#FF3B30] font-bold text-white hover:bg-[#E5352B]"
              onClick={() => handleSubmit('stripe')}
              disabled={submitting !== null}
              data-testid="hotel-pay-stripe"
            >
              {submitting === 'stripe'
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <CreditCard className="w-4 h-4 mr-2" />}
              {t('hotel_pay_card')} · €{t('hotel_price')}
            </Button>
            <Button
              variant="outline"
              className="h-12 rounded-sm border-2 border-[#FF3B30] font-bold text-[#FF3B30] hover:bg-[#FF3B30]/10"
              onClick={() => handleSubmit('dhmad')}
              disabled={submitting !== null}
              data-testid="hotel-pay-dhmad"
            >
              {submitting === 'dhmad' && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              🇹🇳 {t('hotel_pay_dhmad')} ({t('hotel_price_tnd')} TND)
            </Button>
          </div>
        </div>

        {/* Eigene Bestellungen */}
        <div className="space-y-3 border-t border-gray-100 pt-6">
          <h3 className="font-semibold text-[#0A0A0A]">{t('hotel_my_orders')}</h3>
          {bookings.length === 0 ? (
            <p className="text-sm text-gray-500">{t('hotel_no_orders')}</p>
          ) : (
            <ul className="space-y-3">
              {bookings.map((b) => (
                <li key={b.id} className="rounded-sm border border-gray-200 p-4" data-testid="hotel-order">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-semibold text-[#0A0A0A]">
                        {b.city}, {b.destination_country}
                      </p>
                      <p className="text-sm text-gray-600">
                        {formatDate(b.check_in, language)} → {formatDate(b.check_out, language)}
                        {' · '}{t('hotel_appointment')}: {formatDate(b.appointment_date, language)}
                      </p>
                      {b.travelers?.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {b.travelers.map((tr) => `${tr.first_name} ${tr.last_name}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-sm ${STATUS_STYLES[b.status] || 'bg-gray-100 text-gray-600'}`}>
                      {t(`hotel_status_${b.status}`)}
                    </span>
                  </div>

                  {(b.hotel_name || b.booking_reference || b.cancellation_deadline) && b.status === 'delivered' && (
                    <div className="mt-3 text-sm text-gray-600 space-y-1">
                      {b.hotel_name && <p className="font-medium text-[#0A0A0A]">{b.hotel_name}</p>}
                      {b.booking_reference && <p>{t('hotel_reference')}: {b.booking_reference}</p>}
                      {b.cancellation_deadline && (
                        <p className="flex items-center gap-1">
                          <CalendarClock className="w-4 h-4" />
                          {t('hotel_deadline')}: {formatDateTime(b.cancellation_deadline, language)}
                        </p>
                      )}
                    </div>
                  )}

                  <div className="mt-3 flex flex-wrap gap-2">
                    {b.status === 'delivered' && b.has_pdf && (
                      <Button size="sm" className="rounded-sm bg-[#0A0A0A] text-white hover:bg-[#1f1f1f]"
                        onClick={() => handleDownload(b)} disabled={downloadingId === b.id}>
                        {downloadingId === b.id
                          ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                          : <Download className="w-4 h-4 mr-1" />}
                        {t('hotel_download_pdf')}
                      </Button>
                    )}
                    {b.status === 'awaiting_payment' && b.payment_method === 'dhmad' && (
                      <Button asChild size="sm" variant="outline" className="rounded-sm border-[#FF3B30] text-[#FF3B30]">
                        <a href={dhmadUrl} target="_blank" rel="noopener noreferrer">
                          🇹🇳 {t('hotel_pay_now_dhmad')} ({t('hotel_price_tnd')} TND)
                          <ExternalLink className="w-3.5 h-3.5 ml-1" />
                        </a>
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default HotelBookingSection;
